// src/playback-runtime/buffering/useSmartPrebuffer.ts
"use client";

/*
 Predictive audio prebuffer
 Warms network + decoder without autoplay
*/

import { useEffect, useRef } from "react";

type Opts = {
  src: string;
  enabled?: boolean;
};

export function useSmartPrebuffer(
  audioRef: React.RefObject<HTMLAudioElement | null>,
  { src, enabled = true }: Opts
) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (startedRef.current) return;

    const el = audioRef.current;
    if (!el) return;

    // prevent duplicate
    startedRef.current = true;

    let cancelled = false;

    const warmup = async () => {
      try {
        /* ----------- phase 1: connection warmup ----------- */
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "audio";
        link.href = src;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);

        /* ----------- phase 2: range request ----------- */
        await fetch(src, {
          method: "GET",
          headers: { Range: "bytes=0-65535" },
          cache: "force-cache",
        }).catch(() => {});

        if (cancelled) return;

        /* ----------- phase 3: decoder warmup ----------- */
        const prevMuted = el.muted;
        el.muted = true;

        el.preload = "auto";
        el.src = src;
        el.load();

        // load just metadata
        await new Promise<void>((resolve) => {
          const fn = () => {
            el.removeEventListener("loadedmetadata", fn);
            resolve();
          };
          el.addEventListener("loadedmetadata", fn);
        });

        // small decode
        try {
          await el.play();
          el.pause();
        } catch {}

        el.currentTime = 0;
        el.muted = prevMuted;
      } catch {}
    };

    // idle priority
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(warmup, { timeout: 1500 });
    } else {
      setTimeout(warmup, 600);
    }

    return () => {
      cancelled = true;
    };
  }, [audioRef, src, enabled]);
}
